import React, { Component } from "react";
import { withFormik, Form } from "formik";
import * as Yup from "yup";
import DatePicker from "react-datetime-picker";
import "react-datetime-picker/dist/DateTimePicker.css";
import "react-calendar/dist/Calendar.css";
import "react-clock/dist/Clock.css";

import ActionFormButton from "../../../common/ActionFormButton";
import {
  renderRowWithTwoItems,
  renderRowWithSingleItem,
  renderSectionTitle,
  getRowItemObject,
  Section,
  Wrapper,
  Row,
  RowItem,
} from "../../../common/FormUtils";
import Checkbox from "../../../common/Checkbox";
import DaysOfWeek from "../../../common/DaysOfWeek";
import { DateWeek, DayOfWeek } from "../../../../types/Event";
import { Roundtable } from "../../../../types/Roundtable";

const coerceToDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === "object" && typeof value.toDate === "function") {
    const parsed = value.toDate();
    return parsed instanceof Date && !isNaN(parsed.getTime()) ? parsed : null;
  }
  return null;
};

const buildDefaultDays = (): DateWeek[] => [
  { isActive: false, time: 0, dayOfWeek: DayOfWeek.MON },
  { isActive: false, time: 0, dayOfWeek: DayOfWeek.TUE },
  { isActive: false, time: 0, dayOfWeek: DayOfWeek.WED },
  { isActive: false, time: 0, dayOfWeek: DayOfWeek.THU },
  { isActive: false, time: 0, dayOfWeek: DayOfWeek.FRI },
  { isActive: false, time: 0, dayOfWeek: DayOfWeek.SAT },
  { isActive: false, time: 0, dayOfWeek: DayOfWeek.SUN },
];

// Edit mode: the doc stores recurrence.daysOfWeek as plain codes; rehydrate the
// per-day toggle state the picker expects.
const buildDaysFromRoundtable = (item: any): DateWeek[] => {
  const active: DayOfWeek[] = item?.recurrence?.daysOfWeek || [];
  return buildDefaultDays().map((day) => ({
    ...day,
    isActive: active.includes(day.dayOfWeek),
  }));
};

type RoundtableFormValues = {
  id: string;
  title: string;
  goal: string;
  maxParticipants: number | string;
  startTime: Date | string | null;
  endTime: Date | string | null;
  recurringDate: { isActive: boolean; daysOfWeek: DateWeek[] };
};

type Props = {
  setFieldValue: Function;
  onChageFormToEditMode: Function;
  handleSubmit: Function;
  onRemoveItem: Function;
  isSubmitting: boolean;
  mode: string;
  values: RoundtableFormValues;
  // Injected by Formik and consumed by the shared FormUtils render helpers.
  handleChange?: Function;
  handleBlur?: Function;
  touched: any;
  errors: any;
};

class RoundtableForm extends Component<Props> {
  renderDetailsSection = () => {
    const titleField = getRowItemObject(
      "Title",
      "Type the title of the roundtable...",
      "text",
      "title"
    );
    const goalField = getRowItemObject(
      "Goal",
      "What should members leave with? This guides the AI agenda...",
      "textarea",
      "goal"
    );
    const capacityField = getRowItemObject(
      "Capacity (optional)",
      "Max seats. Leave empty for no limit.",
      "number",
      "maxParticipants"
    );

    return (
      <Section>
        {renderSectionTitle("Details")}
        {renderRowWithTwoItems(titleField, capacityField, this.props)}
        {renderRowWithSingleItem(goalField, this.props as any)}
      </Section>
    );
  };

  setActiveDayOfWeek = (index: number) => {
    const { values, setFieldValue } = this.props;
    const daysOfWeek = values.recurringDate.daysOfWeek.map((day, _index) =>
      index === _index ? { ...day, isActive: !day.isActive } : day
    );
    setFieldValue("recurringDate", { ...values.recurringDate, daysOfWeek });
  };

  renderDatesSection = () => {
    const { values, setFieldValue } = this.props;
    const { recurringDate } = values;

    return (
      <Section>
        {renderSectionTitle("Schedule")}
        <Row>
          <RowItem>
            <DatePicker
              value={coerceToDate(values.startTime)}
              onChange={(date) => setFieldValue("startTime", date)}
            />
          </RowItem>
          <RowItem>
            <DatePicker
              value={coerceToDate(values.endTime)}
              onChange={(date) => setFieldValue("endTime", date)}
            />
          </RowItem>
        </Row>
        <Row>
          <RowItem>
            <Checkbox
              type={"checkbox"}
              value={recurringDate.isActive}
              onChange={() =>
                setFieldValue("recurringDate", {
                  ...recurringDate,
                  isActive: !recurringDate.isActive,
                })
              }
              label={"Repeat weekly?"}
            />
          </RowItem>
        </Row>
        {recurringDate.isActive && (
          <div>
            <DaysOfWeek
              // The picker types time as a string; roundtables ignore per-day
              // time (the meeting time comes from startTime), so cast is safe.
              days={recurringDate.daysOfWeek as any}
              setDays={this.setActiveDayOfWeek}
              setTime={() => {}}
            />
          </div>
        )}
      </Section>
    );
  };

  render() {
    const {
      onChageFormToEditMode,
      onRemoveItem,
      isSubmitting,
      mode,
    } = this.props;

    return (
      <Wrapper>
        <Form>
          {this.renderDetailsSection()}
          {this.renderDatesSection()}

          <ActionFormButton
            onChageFormToEditMode={onChageFormToEditMode}
            onClick={this.props.handleSubmit}
            onRemoveItem={onRemoveItem}
            disabled={isSubmitting}
            entity="Roundtable"
            canBeRemoved
            mode={mode}
          />
        </Form>
      </Wrapper>
    );
  }
}

const CustomForm = withFormik<any, RoundtableFormValues>({
  mapPropsToValues: (props: any): RoundtableFormValues => {
    const { item } = props;
    const roundtable = (item || {}) as Partial<Roundtable>;

    return {
      id: roundtable.id || "",
      title: roundtable.title || "",
      goal: roundtable.goal || "",
      maxParticipants: roundtable.maxParticipants ?? "",
      startTime: coerceToDate(roundtable.startTime),
      endTime: coerceToDate(roundtable.endTime),
      recurringDate: {
        isActive: !!roundtable.recurrence?.isActive,
        daysOfWeek: buildDaysFromRoundtable(roundtable),
      },
    };
  },

  validationSchema: () =>
    Yup.lazy(() =>
      Yup.object().shape({
        title: Yup.string().trim().required("Title is required."),
        startTime: Yup.date().nullable().required("Start date is required."),
        endTime: Yup.date()
          .nullable()
          .required("End date is required.")
          .test(
            "after-start",
            "End must be after start.",
            function (endTime) {
              const start = (this.parent as RoundtableFormValues).startTime;
              if (!start || !endTime) return true;
              return new Date(endTime).getTime() > new Date(start).getTime();
            }
          ),
        recurringDate: Yup.object().test(
          "at-least-one-day",
          "Pick at least one weekday for a weekly roundtable.",
          (recurringDate: any) => {
            if (!recurringDate?.isActive) return true;
            return (recurringDate.daysOfWeek || []).some(
              (day: DateWeek) => day.isActive
            );
          }
        ),
      })
    ),

  handleSubmit(values, { setSubmitting, props }) {
    const { onCreateItem, onEditItem, mode } = props;
    const callback = mode === "edit" ? onEditItem : onCreateItem;
    callback({ ...values });
    setSubmitting(false);
  },
})(RoundtableForm);

export default CustomForm;
